import React, { useEffect, useState } from "react";
import styled from "styled-components";
import Copy from "./Copy";

const StyledCollapsible = styled.div`
  margin-bottom: ${({ theme }) => theme.space[2]};

  > h5 {
    margin: 0;
  }
`;

const Button = styled.button`
  display: flex;
  align-items: flex-start;
  width: 100%;
  padding: ${({ theme }) => theme.space[2]};
  border: 0;
  border-radius: ${({ theme }) => theme.radii[1]};
  background-color: ${({ theme }) => theme.colors.secondary};
  color: ${({ theme }) => theme.colors.primary};
  font-family: ${({ theme }) => theme.fonts.base};
  font-size: ${({ theme }) => theme.fontSizes[3]};
  text-align: start;

  &:hover {
    background-color: ${({ theme }) => theme.colors.secondaryTwo};
  }

  &[aria-expanded="true"] {
    svg {
      transform: rotate(180deg);
    }
    border-bottom-right-radius: 0px;
    border-bottom-left-radius: 0px;
    background-color: ${({ theme }) => theme.colors.secondaryTwo};
    font-weight: bold;
  }
`;

const CollapseIcon = styled.div`
  display: flex;
  align-self: stretch;
  align-items: center;
  width: 14px;
  margin-inline-end: 10px;
  flex-shrink: 0;
`;

const CollapsibleContent = styled.div`
  padding: ${({ theme }) => theme.space[2]};
  background-color: ${({ theme }) => theme.colors.white};
  border: ${({ theme }) => theme.borders[4]};
  border-bottom-right-radius: ${({ theme }) => theme.radii[1]};
  border-bottom-left-radius: ${({ theme }) => theme.radii[1]};

  &[aria-hidden] {
    display: none;
  }

  &[aria-hidden="false"] {
    display: block;
  }
`;

// Used to generate unique ids for collapsible content / aria controls
let lastId = 0;
const newId = (prefix = "ism-id") => `${prefix}${++lastId}`;

interface IProps {
  children: React.ReactNode;
  headingText: string;
}

const Collapsible: React.FC<IProps> = ({ children, headingText }) => {
  const [id, setId] = useState<string>("");
  const [open, setOpen] = useState<boolean>(false);

  const toggle = () => setOpen(!open);

  useEffect(() => {
    setId(newId("ism-collapsible-"));
  }, []);

  return (
    <StyledCollapsible>
      <h5>
        <Button
          type="button"
          onClick={toggle}
          aria-expanded={open}
          aria-controls={id}
        >
          <CollapseIcon>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="17"
              height="12"
              viewBox="0 0 17 12"
              fill="none"
            >
              <g clip-path="url(#clip0_94_3540)">
                <path
                  d="M14 4L9.35355 8.64645C9.15829 8.84171 8.84171 8.84171 8.64645 8.64645L4 4"
                  stroke="#001E50"
                  stroke-width="2"
                  stroke-linecap="round"
                />
              </g>
              <defs>
                <clipPath id="clip0_94_3540">
                  <rect
                    width="12"
                    height="17"
                    fill="white"
                    transform="translate(17) rotate(90)"
                  />
                </clipPath>
              </defs>
            </svg>
          </CollapseIcon>
          {headingText}
        </Button>
      </h5>
      <CollapsibleContent id={id} aria-hidden={!open}>
        <Copy>{children}</Copy>
      </CollapsibleContent>
    </StyledCollapsible>
  );
};

export default Collapsible;
